import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import BrandfetchDisplay from './BrandfetchDisplay';
import BrandfetchSinglePageDisplay from './BrandfetchSinglePageDisplay';
import BrandfetchSelector from './BrandfetchSelector';
import BrandfetchGlassmorphicDisplay from './BrandfetchGlassmorphicDisplay';
import { clientLogger } from '../utils/logger';
import { authenticatedFetch, syncUserSession } from '../utils/apiUtils';

/**
 * BrandsManagement component for the admin dashboard
 * Allows administrators to manage and view brand information
 */
const BrandsManagement = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [modalType, setModalType] = useState(null); // 'original', 'single-page', or 'selector'
  const navigate = useNavigate();

  useEffect(() => {
    fetchBrands();
  }, []);

  /**
   * Get user ID from localStorage for API requests
   */
  const getUserId = () => {
    const userDataString = localStorage.getItem('userData');
    if (!userDataString) return null;
    
    try {
      const userData = JSON.parse(userDataString);
      return userData.id;
    } catch (e) {
      clientLogger.error('Failed to parse user data from localStorage', e);
      return null;
    }
  };

  /**
   * Fetch brands data from the API
   */
  const fetchBrands = async (retryCount = 0) => {
    try {
      setLoading(true);
      setError(null);
      
      const userId = getUserId();
      if (!userId) {
        throw new Error('No user ID available');
      }
      
      clientLogger.debug('BrandsManagement: Fetching brands data...', { userId });
      
      // First make sure session is synced
      if (retryCount === 0) {
        await syncUserSession();
      }
      
      const response = await authenticatedFetch(`/api/admin/brands?userId=${userId}`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'X-User-ID': userId
        }
      });

      if (response.status === 401 && retryCount < 2) {
        clientLogger.debug('BrandsManagement: Authentication error, retrying after session sync...');
        
        // Try to sync the session again
        const syncSuccess = await syncUserSession();
        
        if (syncSuccess) {
          clientLogger.debug('BrandsManagement: Session sync successful, retrying...');
          // Wait a bit before retrying
          await new Promise(resolve => setTimeout(resolve, 500));
          return fetchBrands(retryCount + 1);
        } else {
          clientLogger.warn('BrandsManagement: Session sync failed, waiting and retrying anyway');
        }
        
        // If we can't sync or it failed, wait and just retry anyway
        await new Promise(resolve => setTimeout(resolve, 1000));
        return fetchBrands(retryCount + 1);
      }

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `Failed to fetch brands (Status: ${response.status})`);
      }

      const data = await response.json();
      
      // Handle both old format (array) and new format (paginated object)
      let brandsArray;
      if (Array.isArray(data)) {
        // Old format - direct array
        brandsArray = data;
        clientLogger.debug('BrandsManagement: Successfully fetched brands data (old format):', data.length);
      } else if (data.brands && Array.isArray(data.brands)) {
        // New format - paginated response
        brandsArray = data.brands;
        clientLogger.debug('BrandsManagement: Successfully fetched brands data (paginated):', {
          brandsCount: data.brands.length,
          total: data.pagination?.total,
          page: data.pagination?.page,
          totalPages: data.pagination?.totalPages
        });
      } else {
        // Fallback
        brandsArray = [];
        clientLogger.warn('BrandsManagement: Unexpected response format:', data);
      }
      
      setBrands(brandsArray);
    } catch (err) {
      clientLogger.error('Error fetching brands:', err);
      setError(err.message);
      toast.error('Failed to load brands data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle brand selection for detailed view
   */
  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand);
    // Don't navigate to a new URL to avoid issues with closing the modal
    // navigate(`/admin/brands/${brand.id}`);
  };

  /**
   * Handle opening a specific modal type
   */
  const openModal = (brand, type) => {
    setSelectedBrand(brand);
    setModalType(type);
  };

  /**
   * Handle closing the modal
   */
  const closeModal = () => {
    setSelectedBrand(null);
    setModalType(null);
  };

  /**
   * Render the brands list table
   */
  const renderBrandsTable = () => (
    <div className="overflow-x-auto">
      <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Brand Name</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domain</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Organization</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {brands.length === 0 ? (
            <tr>
              <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                No brands found
              </td>
            </tr>
          ) : (
            brands.map((brand) => (
              <tr key={brand.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">{brand.name}</td>
                <td className="px-6 py-4 whitespace-nowrap">{brand.domain}</td>
                <td className="px-6 py-4 whitespace-nowrap">{brand.organization_name}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {new Date(brand.updated_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex space-x-2">
                    <button
                      className="text-blue-600 hover:text-blue-900 px-2 py-1 text-sm border border-blue-600 rounded hover:bg-blue-50"
                      onClick={() => openModal(brand, 'original')}
                    >
                      Tabbed View
                    </button>
                    <button
                      className="text-green-600 hover:text-green-900 px-2 py-1 text-sm border border-green-600 rounded hover:bg-green-50"
                      onClick={() => openModal(brand, 'single-page')}
                    >
                      Single Page
                    </button>
                    <button
                      className="text-purple-600 hover:text-purple-900 px-2 py-1 text-sm border border-purple-600 rounded hover:bg-purple-50"
                      onClick={() => openModal(brand, 'selector')}
                    >
                      Switchable
                    </button>
                    <button
                      className="text-teal-600 hover:text-teal-900 px-2 py-1 text-sm border border-teal-600 rounded hover:bg-teal-50"
                      onClick={() => openModal(brand, 'glassmorphic')}
                    >
                      Glassmorphic View
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  /**
   * Render the appropriate modal based on modalType
   */
  const renderModal = () => {
    if (!selectedBrand || !modalType) return null;
    
    switch (modalType) {
      case 'original':
        return <BrandfetchDisplay domain={selectedBrand.domain} onClose={closeModal} />;
      case 'single-page':
        return <BrandfetchSinglePageDisplay domain={selectedBrand.domain} onClose={closeModal} />;
      case 'selector':
        return <BrandfetchSelector domain={selectedBrand.domain} onClose={closeModal} />;
      case 'glassmorphic':
        return <BrandfetchGlassmorphicDisplay domain={selectedBrand.domain} onClose={closeModal} />;
      default:
        return null;
    }
  };

  /**
   * Main render method
   */
  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-500">Loading brands data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6">
          <p>Error: {error}</p>
          <button 
            className="mt-2 text-blue-600 hover:underline"
            onClick={fetchBrands}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Brands Management</h1>
      
      {renderBrandsTable()}
      {renderModal()}
    </div>
  );
};

export default BrandsManagement; 